import React, { useState, useEffect } from 'react';
import { useConfirm } from '../context/ConfirmContext';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { Person, ContactType, CreatePerson, UpdatePerson } from '../types';
import { personsAPI } from '../services/api';
import PersonsTable from './PersonsTable';
import Pagination from './Pagination';
import { usePagination } from '../hooks/usePagination';
import { saveFiltersToStorage, loadFiltersFromStorage } from '../utils/filterStorage';

const Persons: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [persons, setPersons] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Person | undefined>();
  // Load initial filters from localStorage
  const defaultFilters = { contactTypeFilter: -1, searchText: '' };
  const initialFilters = loadFiltersFromStorage('persons', defaultFilters);

  const [contactTypeFilter, setContactTypeFilter] = useState<number>(initialFilters.contactTypeFilter);
  const [searchText, setSearchText] = useState<string>(initialFilters.searchText);
  const confirm = useConfirm();

  useEffect(() => {
    loadPersons();
  }, []);

  useEffect(() => {
    // Check if we're in edit mode based on URL
    const isEditRoute = location.pathname.startsWith('/persons/edit/');
    const isNewRoute = location.pathname === '/persons/new';

    if (isNewRoute) {
      // Show form for creating new person
      setEditingPerson(undefined);
      setShowForm(true);
    } else if (id && persons.length > 0 && isEditRoute) {
      const personToEdit = persons.find(p => p.id === parseInt(id));
      if (personToEdit) {
        setEditingPerson(personToEdit);
        setShowForm(true);
      }
    } else if (!isEditRoute && !isNewRoute) {
      // Reset form state when not on edit route
      setEditingPerson(undefined);
      setShowForm(false);
    }
  }, [id, persons, location.pathname]);

  const loadPersons = async () => {
    try {
      setLoading(true);
      const data = await personsAPI.getPersons();
      setPersons(data);
      setError(null);
    } catch (err) {
      setError('Failed to load persons');
      console.error('Error loading persons:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePerson = async (personData: CreatePerson) => {
    try {
      await personsAPI.createPerson(personData);
      await loadPersons();
      setShowForm(false);
      setEditingPerson(undefined);
      navigate('/persons');
    } catch (err) {
      setError('Failed to create person');
      console.error('Error creating person:', err);
    }
  };

  const handleUpdatePerson = async (personData: UpdatePerson) => {
    try {
      await personsAPI.updatePerson(personData.id, personData);
      await loadPersons();
      setShowForm(false);
      setEditingPerson(undefined);
      navigate('/persons');
    } catch (err) {
      setError('Failed to update person');
      console.error('Error updating person:', err);
    }
  };

  const handleDeletePerson = async (id: number) => {
    const isConfirmed = await confirm({
      title: 'Delete person',
      message: 'Are you sure you want to delete this person? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!isConfirmed) return;

    try {
      await personsAPI.deletePerson(id);
      await loadPersons();
    } catch (err) {
      setError('Failed to delete person');
      console.error('Error deleting person:', err);
    }
  };

  const handleEditPerson = (person: Person) => {
    // Navigate to edit URL with redirect parameter to return to current location
    navigate(`/persons/edit/${person.id}?redirect=/persons`);
  };

  const handleViewDetails = (id: number) => {
    navigate(`/persons/${id}`);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingPerson(undefined);
    navigate(-1);
  };

  const handleFormSubmit = (personData: CreatePerson | UpdatePerson) => {
    if ('id' in personData) {
      handleUpdatePerson(personData);
    } else {
      handleCreatePerson(personData);
    }
  };

  const filteredPersons = persons.filter(person => {
    const matchesContactType = contactTypeFilter === -1 || person.contactType === contactTypeFilter;
    const matchesSearch = !searchText ||
      person.firstName.toLowerCase().includes(searchText.toLowerCase()) ||
      person.lastName.toLowerCase().includes(searchText.toLowerCase()) ||
      person.notes?.toLowerCase().includes(searchText.toLowerCase()) ||
      person.customers?.some(cp => cp.customerTitle.toLowerCase().includes(searchText.toLowerCase())) ||
      person.emails.some(email => email.address.toLowerCase().includes(searchText.toLowerCase())) ||
      person.phones.some(phone => phone.number.includes(searchText));

    return matchesContactType && matchesSearch;
  });

  const sortedPersons = [...filteredPersons].sort((a, b) => {
    const lastNameCompare = a.lastName.localeCompare(b.lastName, undefined, { sensitivity: 'base' });
    if (lastNameCompare !== 0) return lastNameCompare;
    return a.firstName.localeCompare(b.firstName, undefined, { sensitivity: 'base' });
  });

  const {
    currentPage,
    pageSize,
    paginatedData: paginatedPersons,
    totalItems,
    goToPage,
    setPageSize
  } = usePagination({ data: sortedPersons, initialPageSize: 10 });

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 style={{ 
              color: 'var(--text-primary)', 
              fontSize: 'var(--font-size-3xl)', 
              fontWeight: '700',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              Persons
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-lg)', margin: 0 }}>
              Manage your contacts and people database
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/persons/new')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            Create Person
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div className="spinner-border" role="status"></div>
          <p style={{ color: 'var(--text-muted)' }}>Loading persons...</p>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h5 className="card-title">Person List</h5>
              <div className="ms-auto d-flex flex-wrap gap-2 align-items-end" style={{ justifyContent: 'flex-end' }}>
                <div>
                  <input
                    type="text"
                    id="personSearchText"
                    className="form-control"
                    placeholder="Search persons..."
                    value={searchText}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setSearchText(newValue);
                      saveFiltersToStorage('persons', { contactTypeFilter, searchText: newValue });
                    }}
                  />
                </div>
                <div>
                  <select
                    id="personContactTypeFilter"
                    className="form-select"
                    value={contactTypeFilter}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      setContactTypeFilter(newValue);
                      saveFiltersToStorage('persons', { contactTypeFilter: newValue, searchText });
                    }}
                  >
                    {[
                      { value: -1, label: 'Contact Type' },
                      { value: ContactType.Hot, label: 'Hot' },
                      { value: ContactType.Cold, label: 'Cold' },
                    ].map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <>
              <PersonsTable
                persons={paginatedPersons}
                onEdit={handleEditPerson}
                onDelete={handleDeletePerson}
                onViewDetails={handleViewDetails}
              />
              {totalItems === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem 1rem 0',
                  color: 'var(--text-muted)'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                    <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                  </div>
                  <h5>No persons found</h5>
                  <p>Try adjusting your search filters or create a new person.</p>
                </div>
              ) : (
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalItems}
                  itemsPerPage={pageSize}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  pageSizeOptions={[10, 25, 50, 100]}
                />
              )}
            </>
          </div>
        </div>
      )}
    </div>
  );
};

export default Persons;
